/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../constants';
import axiosInstance from '../../../../services/api';

/**
 * Initial enriched data structure
 */
const initialEnrichedData = {
  cultures: [],
  cultureChildren: [],
  treatmentType: null,
  applicationTechnique: null,
  products: [],
};

/**
 * Hook for managing enriched data with labels
 * Handles bulk fetching for URL state restoration
 *
 * @param {Object} urlState - Current URL state
 * @returns {Object} Enriched data state and setters
 */
export function useEnrichedData(urlState) {
  const [enrichedData, setEnrichedData] = useState(initialEnrichedData);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const lastFetchedStateRef = useRef(null);

  /**
   * Bulk fetches labels for URL state restoration
   */
  const fetchLabelsForURLState = useCallback(
    async (
      cultureIds,
      treatmentTypeId,
      applicationTechniqueId,
      productsArray
    ) => {
      const result = { ...initialEnrichedData };

      const hasAnyUrlData =
        cultureIds?.length > 0 ||
        treatmentTypeId ||
        applicationTechniqueId ||
        productsArray?.length > 0;

      if (!hasAnyUrlData) {
        return result;
      }

      // Build bulk API request
      const bulkRequest = {};

      if (cultureIds?.length > 0) {
        bulkRequest.culture_ids = cultureIds;
      }

      if (treatmentTypeId) {
        bulkRequest.treatment_type_id = treatmentTypeId;
      }

      if (applicationTechniqueId) {
        bulkRequest.application_technique_id = applicationTechniqueId;
      }

      if (productsArray?.length > 0) {
        bulkRequest.product_ids = productsArray
          .map((p) => p.id)
          .filter(Boolean);
        bulkRequest.unit_ids = [
          ...new Set(productsArray.map((p) => p.unit).filter(Boolean)),
        ];
      }

      // Import service dynamically
      const { RiskCalculatorService } = await import(
        '../../../services/riskCalculatorService'
      );
      const bulkData = await RiskCalculatorService.bulkInitialize(bulkRequest);

      // Build enriched cultures
      if (bulkData.cultures?.length > 0) {
        result.cultures = bulkData.cultures.map((c) => ({
          value: c.id,
          id: c.id,
          title: c.french_name || c.english_name || c.latin_name || c.id,
          label: c.french_name || c.english_name || c.latin_name || c.id,
          // SP-442 row 13 — keep all language names so the results table can
          // show the culture name in the language selected on the Cultures step.
          french_name: c.french_name || '',
          english_name: c.english_name || '',
          latin_name: c.latin_name || '',
          // SP-442 row 14 — height shown on hover over the culture name.
          height: c.cultural_height || null,
          has_children: c.has_children || false,
        }));

        // Fetch culture children
        const parentsWithChildren = result.cultures.filter(
          (c) => c.has_children
        );
        if (parentsWithChildren.length > 0) {
          const parentIds = parentsWithChildren.map((p) => p.id).join(',');
          const res = await axiosInstance.get(
            `${API_ENDPOINTS.CULTURES}?parent_ids=${parentIds}&descendants=true`
          );
          const children = res.data?.data || [];

          const parentNameMap = Object.fromEntries(
            parentsWithChildren.map((p) => [p.id, p.title])
          );

          result.cultureChildren = children.map((child) => ({
            value: child.id,
            id: child.id,
            title:
              child.french_name || child.english_name || child.name || child.id,
            label:
              child.french_name || child.english_name || child.name || child.id,
            french_name: child.french_name || '',
            english_name: child.english_name || '',
            latin_name: child.latin_name || '',
            height: child.cultural_height || null,
            parentId: child.parent_id,
            parentName: parentNameMap[child.parent_id],
            isChild: true,
          }));
        }
      }

      // Treatment type and application technique
      if (bulkData.treatment_type) {
        result.treatmentType = bulkData.treatment_type;
      }

      if (bulkData.application_technique) {
        result.applicationTechnique = bulkData.application_technique;
      }

      // Products with units
      if (bulkData.products?.length > 0 && productsArray?.length > 0) {
        const unitMap = new Map((bulkData.units || []).map((u) => [u.id, u]));
        const productMap = new Map(bulkData.products.map((p) => [p.id, p]));

        result.products = productsArray.map((p) => {
          const product = productMap.get(p.id) || { id: p.id, name: p.id };
          const unit = unitMap.get(p.unit) || { id: p.unit, name: p.unit };
          // Get doses from product API data if available
          const doses = product.doses || [];
          return {
            id: product.id,
            name: product.name,
            approvalNumber: product.approval_number || '',
            productTypes: product.product_types || [],
            dose: p.dose,
            unit: p.unit,
            // SP-442 row 30 — display_name carries « Culture ou précision » for the tooltip.
            unitName: unit.display_name || unit.name,
            unitBareName: unit.name,
            // Format doses for display
            listedDosages: doses.length > 0 ? doses.join(' • ') : null,
            // SP-442 row 22 — Bio / Prescription agronomique badges.
            isOrganic: product.is_organic_approval || false,
            hasPrescription: product.agronomic_prescription_icon || false,
            prescriptionComment: product.agronomic_prescription_comment || '',
            // SP-442 row 23 — active substance names (rich text) for the hover.
            activeSubstances: product.active_substances || [],
          };
        });
      }

      return result;
    },
    []
  );

  // Fetch labels when URL state changes
  useEffect(() => {
    const hasUrlData =
      (urlState.cultures && urlState.cultures.length > 0) ||
      urlState.treatmentType ||
      urlState.applicationTechnique ||
      (urlState.products && urlState.products.length > 0);

    if (!hasUrlData) {
      setIsLoadingLabels(false);
      return;
    }

    // Create stable key for deduplication
    const currentStateKey = JSON.stringify({
      cultures: [...(urlState.cultures || [])].sort(),
      treatmentType: urlState.treatmentType,
      applicationTechnique: urlState.applicationTechnique,
      // Doses are excluded: they change on every keystroke and the payload does not depend on them.
      products: (urlState.products || []).map((p) => ({
        id: p.id,
        unit: p.unit,
      })),
    });

    if (lastFetchedStateRef.current === currentStateKey) {
      return;
    }

    lastFetchedStateRef.current = currentStateKey;
    const key = currentStateKey;
    setIsLoadingLabels(true);

    fetchLabelsForURLState(
      urlState.cultures,
      urlState.treatmentType,
      urlState.applicationTechnique,
      urlState.products
    )
      .then((newEnrichedData) => {
        if (lastFetchedStateRef.current !== key) {
          return;
        }
        // Merge new data while preserving sageDoses from existing products
        setEnrichedData((prev) => {
          // If we have existing products with sageDoses, preserve them
          if (prev.products?.length > 0 && newEnrichedData.products?.length > 0) {
            const existingProductsMap = new Map(
              prev.products.map((p) => [p.id, p])
            );
            newEnrichedData.products = newEnrichedData.products.map((p) => {
              const existing = existingProductsMap.get(p.id);
              if (existing?.sageDoses?.length > 0) {
                return {
                  ...p,
                  sageDoses: existing.sageDoses,
                  listedDosages: existing.listedDosages,
                };
              }
              return p;
            });
          }
          return newEnrichedData;
        });
        setIsLoadingLabels(false);
      })
      .catch((error) => {
        if (lastFetchedStateRef.current !== key) {
          return;
        }
        console.error('[URL State Restore] Failed to fetch labels:', error);
        setIsLoadingLabels(false);
      });
  }, [
    urlState.cultures,
    urlState.treatmentType,
    urlState.applicationTechnique,
    urlState.products,
    fetchLabelsForURLState,
  ]);

  // Reset enriched data
  const resetEnrichedData = useCallback(() => {
    setEnrichedData(initialEnrichedData);
  }, []);

  // Update specific parts of enriched data
  const updateEnrichedCultures = useCallback((cultures) => {
    setEnrichedData((prev) => ({ ...prev, cultures }));
  }, []);

  const updateEnrichedTreatmentType = useCallback((treatmentType) => {
    setEnrichedData((prev) => ({ ...prev, treatmentType }));
  }, []);

  const updateEnrichedApplicationTechnique = useCallback(
    (applicationTechnique) => {
      setEnrichedData((prev) => ({ ...prev, applicationTechnique }));
    },
    []
  );

  const updateEnrichedProducts = useCallback((products) => {
    setEnrichedData((prev) => ({ ...prev, products }));
  }, []);

  const clearDependentEnrichedData = useCallback((level) => {
    if (level === 'cultures') {
      setEnrichedData((prev) => ({
        ...prev,
        treatmentType: null,
        applicationTechnique: null,
        products: [],
      }));
    } else if (level === 'treatmentType') {
      setEnrichedData((prev) => ({
        ...prev,
        applicationTechnique: null,
        products: [],
      }));
    } else if (level === 'applicationTechnique') {
      setEnrichedData((prev) => ({
        ...prev,
        products: [],
      }));
    }
  }, []);

  return {
    enrichedData,
    isLoadingLabels,
    resetEnrichedData,
    updateEnrichedCultures,
    updateEnrichedTreatmentType,
    updateEnrichedApplicationTechnique,
    updateEnrichedProducts,
    clearDependentEnrichedData,
  };
}

export default useEnrichedData;
